import React from 'react';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { Space, Tag } from 'antd';
import { getAttachmentDisplayText, getAttachmentTitleText } from './composerShared.jsx';

function getAttachmentStatus(file) {
  return String(file?.status || '').trim().toLowerCase();
}

function hasAttachmentSourceId(file) {
  return Boolean(String(file?.source_id || file?.id || '').trim());
}

function isAttachmentReady(file) {
  return getAttachmentStatus(file) === 'ready' && hasAttachmentSourceId(file);
}

function isAttachmentFailed(file) {
  const status = getAttachmentStatus(file);
  return status === 'failed' || status === 'error' || status.startsWith('failed');
}

export function resolveAttachmentProgress(file) {
  const status = getAttachmentStatus(file);
  if (status === 'ready' && hasAttachmentSourceId(file)) {
    return { percent: 100, tone: 'ready' };
  }
  if (isAttachmentFailed(file)) {
    return { percent: 100, tone: 'failed' };
  }
  const progressByStatus = {
    uploading: 25,
    uploaded: 45,
    processing: 60,
    parsing: 60,
    chunked: 75,
    embedded: 90,
    indexed: 96,
  };
  return {
    percent: progressByStatus[status] || 12,
    tone: 'active',
  };
}

export function resolveAttachmentTypeLabel(file) {
  const record = Object.assign({}, file);
  const mimeType = String([record.mime_type, record.type].find(Boolean) ?? '').trim().toLowerCase();
  const rawName = String([record.name, record.filename].find(Boolean) ?? '').trim();
  const extension = rawName.includes('.')
    ? rawName.split('.').pop().trim().toLowerCase()
    : '';
  if (extension) {
    return extension.slice(0, 5).toUpperCase();
  }
  const mimeLabels = [
    { matches: ['pdf'], label: 'PDF' },
    { matches: ['word'], label: 'DOC' },
    { matches: ['spreadsheet', 'excel'], label: 'XLS' },
    { matches: ['image'], label: 'IMG' },
    { matches: ['text'], label: 'TXT' },
  ];
  return mimeLabels.find(({ matches }) => matches.some((match) => mimeType.includes(match)))?.label ?? 'FILE';
}

function resolveProgressColor() {
  return 'rgba(47, 111, 236, 0.12)';
}

function resolveBorderColor(themeTokens) {
  return themeTokens.fileTagBorder;
}

function resolveProgressBackground(progress, themeTokens) {
  const percent = Math.max(0, Math.min(100, Number(progress?.percent) || 0));
  const fillColor = resolveProgressColor();
  const baseColor = themeTokens.fileTagBg;
  return `linear-gradient(90deg, ${fillColor} 0%, ${fillColor} ${percent}%, ${baseColor} ${percent}%, ${baseColor} 100%)`;
}

export default function ComposerAttachmentTags({
  fileList = [],
  onRemoveFile,
  themeTokens,
  attachmentConstraintsHint,
}) {
  if (!Array.isArray(fileList) || fileList.length === 0) {
    return null;
  }

  return (
    <div
      aria-label={attachmentConstraintsHint}
      style={{ marginBottom: 8, width: '100%' }}
    >
      <Space size={[8, 8]} style={{ width: '100%' }} wrap>
        {fileList.map((file) => {
          const isFailed = isAttachmentFailed(file);
          const isReady = isAttachmentReady(file);
          const progress = resolveAttachmentProgress(file);
          const typeLabel = resolveAttachmentTypeLabel(file);
          const titleText = getAttachmentTitleText(file);
          const statusIcon = isReady
            ? <CheckCircleFilled />
            : (isFailed ? <ExclamationCircleFilled /> : <LoadingOutlined spin />);
          return (
            <Tag
              aria-label={titleText}
              className="flare-file-tag"
              closable
              icon={statusIcon}
              key={file.uid || file.name}
              onClose={(event) => {
                event.preventDefault();
                onRemoveFile?.(file);
              }}
              style={{
                alignItems: 'center',
                background: resolveProgressBackground(progress, themeTokens),
                border: `1px solid ${resolveBorderColor(themeTokens)}`,
                borderRadius: 12,
                color: themeTokens.fileTagText,
                display: 'inline-flex',
                fontSize: 13,
                gap: 6,
                lineHeight: '20px',
                maxWidth: 320,
                minHeight: 32,
                overflow: 'hidden',
                padding: '3px 7px 3px 9px',
                position: 'relative',
              }}
              title={titleText}
            >
              <span
                aria-label={`文件类型 ${typeLabel}`}
                style={{
                  background: 'rgba(47, 111, 236, 0.1)',
                  borderRadius: 5,
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: '16px',
                  padding: '0 5px',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {typeLabel}
              </span>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 210,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  verticalAlign: 'bottom',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                }}
                title={titleText}
              >
                {getAttachmentDisplayText(file)}
              </span>
            </Tag>
          );
        })}
      </Space>
    </div>
  );
}
